import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { authAPI } from '../services/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    // Protection contre le double-appel (React StrictMode / prefetch)
    if (calledRef.current) return;
    calledRef.current = true;

    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Token de vérification manquant');
      return;
    }

    const doVerify = async () => {
      try {
        await authAPI.verifyEmail(token);
        setStatus('success');
        setMessage('Votre email a été vérifié avec succès !');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/connexion');
        }, 3000);
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Erreur lors de la vérification. Le lien est peut-être expiré ou invalide.');
      }
    };

    doVerify();
  }, [searchParams, navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_2bde1c9b-86e8-45d3-80bd-0f0476a263ab/artifacts/ur3i0a8o_BAckground.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="verify-email-page"
    >
      <div className="absolute inset-0 bg-[#0A0A0B]/90" />
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-[#1A1A1D] p-10 rounded-lg metallic-border cyber-glow text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="h-16 w-16 text-[#00FFFF] mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-4">Vérification en cours...</h1>
              <p className="text-[#B8B8BA]">Veuillez patienter pendant que nous vérifions votre email.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="inline-block p-4 bg-[#00FF88]/10 rounded-full mb-6">
                <CheckCircle className="h-16 w-16 text-[#00FF88]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Email vérifié !</h1>
              <p className="text-[#B8B8BA] mb-6">{message}</p>
              <p className="text-[#00FFFF] text-sm">Redirection vers la connexion...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="inline-block p-4 bg-[#FF3B5C]/10 rounded-full mb-6">
                <XCircle className="h-16 w-16 text-[#FF3B5C]" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Erreur de vérification</h1>
              <p className="text-[#B8B8BA] mb-6">{message}</p>
              <button
                onClick={() => navigate('/connexion')}
                className="btn-primary"
                data-testid="back-to-login"
              >
                Retour à la connexion
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
